import { defineStore } from 'pinia';
import { useFetchApi } from '@/composables/common/useFetchApi';
import { ref, computed } from 'vue';
import Mater from '@/api/Master';
import type { Team, Stadium, BallType, Category, Plan, Result, EveTeam, UserData } from '@/types';
import ls from 'localstorage-ttl';
import { find as _find } from 'lodash';
import { compress, type Compressed, decompress } from 'compress-json';

const MASTER_TEAM_KEY = 'master_team_cache';
const MASTER_STADIUM_KEY = 'master_stadium_cache';
const MASTER_BALL_TYPE_KEY = 'master_ball_type_cache';
const MASTER_CATEGORY_KEY = 'master_category_cache';
const MASTER_PLAN_KEY = 'master_plan_cache';
const MASTER_RESULT_KEY = 'master_result_cache';
const MASTER_EVE_TEAM_KEY = 'master_eve_team_cache';
const MASTER_GROUP_USERS_KEY = 'master_group_users_cache';
const MASTER_DATA_EXPIRY = 86400 * 1000; // 1 days in seconds (1日間)

export const useMasterStore = defineStore('master', () => {
  /**
   * state
   */
  const team = ref<Team[]>([]);
  const stadium = ref<Stadium[]>([]);
  const ball_type = ref<BallType[]>([]);
  const category = ref<Category[]>([]);
  const plan = ref<Plan[]>([]);
  const result = ref<Result[]>([]);
  const eve_team = ref<EveTeam[]>([]);
  const group_users = ref<UserData[]>([]);

  /**
   * private variable
   */
  const _teamById = ref<Map<string, Team>>(new Map());

  /**
   * getters
   */
  const getTeamList = computed(() => team.value);
  const getStadiumList = computed(() => stadium.value);
  const getBallTypeList = computed(() => ball_type.value);
  const getCategoryList = computed(() => category.value);
  const getPlanList = computed(() => plan.value);
  const getResultList = computed(() => result.value);
  const getEveTeamList = computed(() => eve_team.value);
  const getGroupUsers = computed(() => group_users.value);

  const findTeam = computed(() => {
    return (id: string) => _teamById.value.get(id);
  });

  const findResult = computed(() => {
    return (id: number) => {
      return _find(result.value, { _id: `result-${id}` });
    };
  });

  const findStadium = computed(() => {
    return (id: string) => _find(stadium.value, { _id: `stadium-${id}` });
  });

  const findBallType = computed(() => {
    return (id: string) => _find(ball_type.value, { _id: id });
  });

  const findPlan = computed(() => {
    return (id: string) => _find(plan.value, { _id: id });
  });

  const findEveTeam = computed(() => {
    return (id: number) => _find(eve_team.value, { group_id: id });
  });

  /**
   * actions
   */
  const fetchMasterData = async () => {
    // Try to load each master list from its own localStorage key
    const teamCache = ls.get(MASTER_TEAM_KEY);
    const stadiumCache = ls.get(MASTER_STADIUM_KEY);
    const ballTypeCache = ls.get(MASTER_BALL_TYPE_KEY);
    const categoryCache = ls.get(MASTER_CATEGORY_KEY);
    const planCache = ls.get(MASTER_PLAN_KEY);
    const resultCache = ls.get(MASTER_RESULT_KEY);
    const eveTeamCache = ls.get(MASTER_EVE_TEAM_KEY);
    const groupUsersCache = ls.get(MASTER_GROUP_USERS_KEY);

    if (
      teamCache &&
      stadiumCache &&
      ballTypeCache &&
      categoryCache &&
      planCache &&
      resultCache &&
      eveTeamCache &&
      groupUsersCache
    ) {
      team.value = decompress(<Compressed>teamCache) as Team[];
      stadium.value = decompress(<Compressed>stadiumCache) as Stadium[];
      ball_type.value = decompress(<Compressed>ballTypeCache) as BallType[];
      category.value = decompress(<Compressed>categoryCache) as Category[];
      plan.value = decompress(<Compressed>planCache) as Plan[];
      result.value = decompress(<Compressed>resultCache) as Result[];
      eve_team.value = decompress(<Compressed>eveTeamCache) as EveTeam[];
      group_users.value = decompress(<Compressed>groupUsersCache) as UserData[];
      _teamById.value = new Map(team.value.map((t) => [t._id, t]));
      return; // APIリクエストは行わない
    }

    const { fetchData, error, data } = useFetchApi();
    await fetchData(Mater.getMasterList);

    if (error.value) {
      throw error.value;
    }

    const response = data.value;
    if (
      response?.data?.team &&
      response?.data?.stadium &&
      response?.data?.ball_type &&
      response?.data?.category &&
      response?.data?.plan &&
      response?.data?.result &&
      response?.data?.eve_team &&
      response?.data?.group_users
    ) {
      team.value = response.data.team;
      _teamById.value = new Map(team.value.map((team) => [team._id, team]));
      stadium.value = response.data.stadium;
      ball_type.value = response.data.ball_type;
      category.value = response.data.category;
      plan.value = response.data.plan;
      result.value = response.data.result;
      eve_team.value = response.data.eve_team;
      group_users.value = response.data.group_users;

      // Save each list into its own localStorage key with TTL
      ls.set(MASTER_TEAM_KEY, compress(team.value), MASTER_DATA_EXPIRY);
      ls.set(MASTER_STADIUM_KEY, compress(stadium.value), MASTER_DATA_EXPIRY);
      ls.set(MASTER_BALL_TYPE_KEY, compress(ball_type.value), MASTER_DATA_EXPIRY);
      ls.set(MASTER_CATEGORY_KEY, compress(category.value), MASTER_DATA_EXPIRY);
      ls.set(MASTER_PLAN_KEY, compress(plan.value), MASTER_DATA_EXPIRY);
      ls.set(MASTER_RESULT_KEY, compress(result.value), MASTER_DATA_EXPIRY);
      ls.set(MASTER_EVE_TEAM_KEY, compress(eve_team.value), MASTER_DATA_EXPIRY);
      ls.set(MASTER_GROUP_USERS_KEY, compress(group_users.value), MASTER_DATA_EXPIRY);
    } else {
      console.warn('Master data API did not return data in the expected format.');
    }
  };

  const removeMasterDataCache = async () => {
    ls.remove(MASTER_TEAM_KEY);
    ls.remove(MASTER_STADIUM_KEY);
    ls.remove(MASTER_BALL_TYPE_KEY);
    ls.remove(MASTER_CATEGORY_KEY);
    ls.remove(MASTER_PLAN_KEY);
    ls.remove(MASTER_RESULT_KEY);
    ls.remove(MASTER_EVE_TEAM_KEY);
    ls.remove(MASTER_GROUP_USERS_KEY);
  };

  return {
    getTeamList,
    getStadiumList,
    getBallTypeList,
    getCategoryList,
    getPlanList,
    getResultList,
    getEveTeamList,
    getGroupUsers,
    findTeam,
    findResult,
    findStadium,
    findBallType,
    findPlan,
    findEveTeam,
    fetchMasterData,
    removeMasterDataCache
  };
});
